import React, { useMemo } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ApexOptions } from 'apexcharts';
import { Alert, Spin, Tag, Tooltip, Typography } from 'antd';
import Chart from 'react-apexcharts';

import { inspectAssessmentPeriodPriceKey } from '../../../../state/app/inspect_assessment/actions';
import type {
  InspectAssessmentPeriod,
  InspectAssessmentPeriodPrices,
  InspectAssessmentState,
  InspectAssessmentUserAssignment,
} from '../../../../state/app/inspect_assessment/types';
import {
  assessmentPeriodMarketChanges,
  finiteOrNull,
  formatPrice,
  formatYield,
  pmChartPrice,
  relevantPeriodPriceSeries,
} from './chartData';
import { formatAssessmentDate } from './formatters';
import { describeIcs } from './ics';
import PeriodChangeTag from './PeriodChangeTag';
import UserNewsItem from './UserNewsItem';
import {
  buildUserAssignmentIndex,
  describeUserSelection,
} from './userAssignments';

const PERIOD_WIDTH = 280;
const AXIS_GUTTER = 72;

type PeriodEntry = {
  ordinal: number;
  period: InspectAssessmentPeriod;
  prices?: InspectAssessmentPeriodPrices;
  priceError?: string;
  priceLoading: boolean;
  newsLoading: boolean;
};

type TimelineRange = {
  start: number;
  end: number;
};

type TimelinePoint = {
  x: number;
  y: number | null;
};

type TimelineSeries = {
  name: string;
  type: 'line';
  data: TimelinePoint[];
};

type SeriesMeasure = 'price' | 'yield';

type SeriesBucket = {
  assetId: number;
  assetName: string;
  measure: SeriesMeasure;
  pointsByPeriod: Map<number, TimelinePoint[]>;
};

const parseDate = (value?: string) => {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

const formatIcsSum = (value?: number) =>
  value === undefined ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)}`;

const periodRanges = (entries: PeriodEntry[]): Array<TimelineRange | null> =>
  entries.map((entry, ordinal) => {
    const start = parseDate(
      entry.prices?.start_date ?? entry.period.scenario_date,
    );
    const end = parseDate(
      entry.prices?.end_date ??
        entries[ordinal + 1]?.period.scenario_date ??
        entry.period.assessment_period?.end_date,
    );
    if (start === undefined || end === undefined || end <= start) return null;
    return { start, end };
  });

const timelineCoordinate = (
  timestamp: string,
  ordinal: number,
  range: TimelineRange | null,
) => {
  if (!range) return undefined;
  const parsed = parseDate(timestamp);
  if (parsed === undefined) return undefined;
  const progress = Math.min(
    1,
    Math.max(0, (parsed - range.start) / (range.end - range.start)),
  );
  return ordinal + progress;
};

const formatTimelineCoordinate = (
  coordinate: number,
  ranges: Array<TimelineRange | null>,
) => {
  if (!Number.isFinite(coordinate) || ranges.length === 0) return '';
  const ordinal = Math.min(
    ranges.length - 1,
    Math.max(0, Math.floor(coordinate)),
  );
  const range = ranges[ordinal];
  if (!range) return '';
  const progress =
    coordinate >= ranges.length
      ? 1
      : Math.min(1, Math.max(0, coordinate - ordinal));
  const timestamp = range.start + (range.end - range.start) * progress;
  return formatAssessmentDate(new Date(timestamp).toISOString());
};

const addBucketPoints = (
  buckets: Map<string, SeriesBucket>,
  assetId: number,
  assetName: string,
  measure: SeriesMeasure,
  ordinal: number,
  points: TimelinePoint[],
) => {
  if (points.length === 0) return;
  const key = `${measure}:${assetId}`;
  const bucket = buckets.get(key) ?? {
    assetId,
    assetName,
    measure,
    pointsByPeriod: new Map<number, TimelinePoint[]>(),
  };
  bucket.pointsByPeriod.set(ordinal, points);
  buckets.set(key, bucket);
};

const flattenBucketPoints = (bucket: SeriesBucket) => {
  const periodPoints = Array.from(bucket.pointsByPeriod.entries()).sort(
    ([left], [right]) => left - right,
  );
  const flattened: TimelinePoint[] = [];
  let previousOrdinal: number | undefined;

  periodPoints.forEach(([ordinal, points]) => {
    if (previousOrdinal !== undefined && ordinal > previousOrdinal + 1) {
      flattened.push(
        { x: previousOrdinal + 0.999999, y: null },
        { x: ordinal + 0.000001, y: null },
      );
    }
    flattened.push(...points);
    previousOrdinal = ordinal;
  });

  const deduplicated = new Map<string, TimelinePoint>();
  flattened.forEach(point => {
    deduplicated.set(
      `${point.x}:${point.y === null ? 'null' : 'value'}`,
      point,
    );
  });
  return Array.from(deduplicated.values()).sort(
    (left, right) => left.x - right.x,
  );
};

const buildChart = (
  entries: PeriodEntry[],
  ranges: Array<TimelineRange | null>,
) => {
  const buckets = new Map<string, SeriesBucket>();

  entries.forEach(entry => {
    if (entry.prices?.status !== 'available') return;
    relevantPeriodPriceSeries(entry.prices).forEach(asset => {
      const assetName =
        asset.ticker || asset.name || `Asset #${asset.underlying_asset_id}`;
      const prices = asset.points
        .map(point => {
          const x = timelineCoordinate(
            point.timestamp,
            entry.ordinal,
            ranges[entry.ordinal],
          );
          const y = pmChartPrice(point, asset.is_fx_forward);
          return x === undefined || y === null ? undefined : { x, y };
        })
        .filter((point): point is TimelinePoint => point !== undefined);
      addBucketPoints(
        buckets,
        asset.underlying_asset_id,
        assetName,
        'price',
        entry.ordinal,
        prices,
      );

      if (asset.is_bond) {
        const yields = asset.points
          .map(point => {
            const x = timelineCoordinate(
              point.timestamp,
              entry.ordinal,
              ranges[entry.ordinal],
            );
            const y = finiteOrNull(point.ask_yield);
            return x === undefined || y === null ? undefined : { x, y };
          })
          .filter((point): point is TimelinePoint => point !== undefined);
        addBucketPoints(
          buckets,
          asset.underlying_asset_id,
          assetName,
          'yield',
          entry.ordinal,
          yields,
        );
      }
    });
  });

  const uniqueAssetIds = new Set(
    Array.from(buckets.values()).map(bucket => bucket.assetId),
  );
  const orderedBuckets = Array.from(buckets.values()).sort(
    (left, right) =>
      Number(left.measure === 'yield') - Number(right.measure === 'yield') ||
      left.assetId - right.assetId,
  );
  const seriesForMeasure = (measure: SeriesMeasure): TimelineSeries[] =>
    orderedBuckets
      .filter(bucket => bucket.measure === measure)
      .map(bucket => ({
        name: `${bucket.assetName}${
          uniqueAssetIds.size > 1 ? ` #${bucket.assetId}` : ''
        } ${measure === 'price' ? 'price' : 'ask yield'}`,
        type: 'line' as const,
        data: flattenBucketPoints(bucket),
      }));

  const priceSeries = seriesForMeasure('price');
  const yieldSeries = seriesForMeasure('yield');
  return {
    priceSeries,
    yieldSeries,
    series: [...priceSeries, ...yieldSeries],
  };
};

type Props = {
  state: InspectAssessmentState;
  userAssignments: InspectAssessmentUserAssignment[];
  userAssignmentsFetching: boolean;
  hasSelectedUserAttempt: boolean;
};

const FullAssessmentWorkspace: React.FC<Props> = ({
  state,
  userAssignments,
  userAssignmentsFetching,
  hasSelectedUserAttempt,
}) => {
  const instrument = state.instruments.find(
    item => item.id === state.selectedInstrumentId,
  );
  const entries = useMemo<PeriodEntry[]>(
    () =>
      state.periods.map((period, ordinal) => {
        const priceKey =
          state.selectedAssessmentId !== undefined && instrument
            ? inspectAssessmentPeriodPriceKey(
                state.selectedAssessmentId,
                period.index,
                instrument.id,
              )
            : undefined;
        return {
          ordinal,
          period,
          prices: priceKey ? state.periodPricesByKey[priceKey] : undefined,
          priceError: priceKey
            ? state.periodPricesErrorByKey[priceKey]
            : undefined,
          priceLoading: Boolean(
            priceKey &&
              (state.periodPricesFetchingByKey[priceKey] ||
                state.periodPricesBatchFetchingByKey[priceKey]),
          ),
          newsLoading: Boolean(
            state.periodNewsBatchFetchingByIndex[period.index] ||
              (state.periodNewsFetching &&
                state.periodNewsPeriodIndex === period.index),
          ),
        };
      }),
    [
      instrument,
      state.periodNewsBatchFetchingByIndex,
      state.periodNewsFetching,
      state.periodNewsPeriodIndex,
      state.periodPricesBatchFetchingByKey,
      state.periodPricesByKey,
      state.periodPricesErrorByKey,
      state.periodPricesFetchingByKey,
      state.periods,
      state.selectedAssessmentId,
    ],
  );
  const ranges = useMemo(() => periodRanges(entries), [entries]);
  const chart = useMemo(() => buildChart(entries, ranges), [entries, ranges]);
  const userAssignmentIndex = useMemo(
    () => buildUserAssignmentIndex(userAssignments, state.periods),
    [state.periods, userAssignments],
  );
  const periodMetricsByIndex = useMemo(
    () =>
      new Map(
        entries.map(entry => {
          const periodNews = state.periodNewsByIndex[entry.period.index];
          const allRows = (periodNews?.items ?? []).map(news => ({
            news,
            ics: describeIcs(news, instrument),
            userSelection: describeUserSelection(
              userAssignmentIndex,
              news.id,
              instrument?.id,
              entry.period.index,
            ),
          }));
          const rows = allRows
            .filter(
              row => row.ics.tone === 'positive' || row.ics.tone === 'negative',
            )
            .sort(
              (left, right) => (right.ics.value ?? 0) - (left.ics.value ?? 0),
            );
          return [
            entry.period.index,
            {
              icsSum: periodNews
                ? rows.reduce((sum, row) => sum + (row.ics.value ?? 0), 0)
                : undefined,
              userSelectedNonNeutralNewsCount: rows.filter(
                row => row.userSelection.selected,
              ).length,
              pricePercentage: assessmentPeriodMarketChanges(entry.prices)
                .pricePercentage,
              rows,
              otherUserSelectedRows: allRows.filter(
                row =>
                  row.userSelection.selected &&
                  row.ics.tone !== 'positive' &&
                  row.ics.tone !== 'negative',
              ),
            },
          ] as const;
        }),
      ),
    [entries, instrument, state.periodNewsByIndex, userAssignmentIndex],
  );
  const loadedPricePeriods = entries.filter(entry => entry.prices).length;
  const loadedNewsPeriods = entries.filter(
    entry => state.periodNewsByIndex[entry.period.index],
  ).length;
  const allPricesSettled = entries.every(
    entry => entry.prices || entry.priceError,
  );
  const selectedPeriodPriceError = entries.find(
    entry => entry.period.index === state.selectedPeriodIndex,
  )?.priceError;
  const dataStillLoading =
    entries.length > 0 &&
    !state.periodBatchError &&
    !state.periodNewsError &&
    !selectedPeriodPriceError &&
    (loadedPricePeriods < entries.length || loadedNewsPeriods < entries.length);
  const timelineWidth = entries.length * PERIOD_WIDTH + AXIS_GUTTER * 2;
  const alignedGridStyle = {
    gridTemplateColumns: `repeat(${entries.length}, ${PERIOD_WIDTH}px)`,
    paddingInline: AXIS_GUTTER,
  };

  const options = useMemo<ApexOptions>(() => {
    const priceSeriesNames = chart.priceSeries.map(series => series.name);
    const yieldSeriesNames = chart.yieldSeries.map(series => series.name);
    const hasPriceData = priceSeriesNames.length > 0;
    const hasYieldData = yieldSeriesNames.length > 0;
    const priceAxis: ApexYAxis = {
      forceNiceScale: true,
      seriesName: priceSeriesNames,
      title: { text: 'Price' },
      labels: {
        formatter: formatPrice,
        maxWidth: 52,
        minWidth: 52,
      },
    };
    const yieldAxis: ApexYAxis = {
      decimalsInFloat: 3,
      forceNiceScale: true,
      opposite: true,
      seriesName: yieldSeriesNames,
      title: { text: 'Ask yield (%)' },
      labels: {
        formatter: formatYield,
        maxWidth: 52,
        minWidth: 52,
      },
    };

    return {
      chart: {
        animations: { enabled: false },
        parentHeightOffset: 0,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      grid: {
        padding: {
          bottom: 0,
          left: 0,
          right: hasYieldData ? 0 : AXIS_GUTTER,
          top: 0,
        },
      },
      legend: {
        fontSize: '11px',
        horizontalAlign: 'left',
        position: 'top',
      },
      markers: { size: 0 },
      stroke: { curve: 'straight', width: 2 },
      tooltip: {
        hideEmptySeries: true,
        shared: true,
        x: {
          formatter: value => formatTimelineCoordinate(Number(value), ranges),
        },
        y: [
          ...priceSeriesNames.map(() => ({ formatter: formatPrice })),
          ...yieldSeriesNames.map(() => ({ formatter: formatYield })),
        ],
      },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { show: false },
        max: entries.length,
        min: 0,
        tickAmount: entries.length,
        tooltip: { enabled: false },
        type: 'numeric',
      },
      yaxis:
        hasPriceData && hasYieldData
          ? [priceAxis, yieldAxis]
          : hasYieldData
            ? yieldAxis
            : priceAxis,
    };
  }, [chart.priceSeries, chart.yieldSeries, entries.length, ranges]);

  if (state.periodsFetching || state.instrumentsFetching) {
    return (
      <div className="assessment-inspect-assessment__full-empty">
        <Spin size="small" />
      </div>
    );
  }
  if (state.periodsError || state.instrumentsError) {
    return (
      <div className="assessment-inspect-assessment__full-empty">
        <Alert
          description={state.periodsError || state.instrumentsError}
          message="Unable to build the full assessment timeline"
          showIcon
          type="error"
        />
      </div>
    );
  }
  if (state.selectedAssessmentId === undefined) {
    return (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          Select an assessment to see its full timeline.
        </Typography.Text>
      </div>
    );
  }
  if (!instrument) {
    return (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          Select an assessed instrument to see its full timeline.
        </Typography.Text>
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          No assessment periods are available.
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className="assessment-inspect-assessment__full-workspace">
      <div className="assessment-inspect-assessment__full-heading">
        <div className="assessment-inspect-assessment__full-heading-copy">
          <Typography.Text strong>
            {instrument.code} full price and news timeline
          </Typography.Text>
        </div>
        {dataStillLoading && (
          <Spin
            className="assessment-inspect-assessment__full-load-status"
            size="small"
          />
        )}
      </div>
      {state.periodBatchError && (
        <Alert
          banner
          message="Some timeline data could not be loaded"
          description={state.periodBatchError}
          showIcon
          type="warning"
        />
      )}
      <div
        aria-label={`${instrument.code} full timeline; scroll horizontally to inspect every period`}
        className="assessment-inspect-assessment__full-scroll"
        tabIndex={0}
      >
        <div
          className="assessment-inspect-assessment__full-timeline"
          style={{ width: timelineWidth }}
        >
          <div
            className="assessment-inspect-assessment__full-period-track"
            style={alignedGridStyle}
          >
            {entries.map(entry => {
              const priceIssue =
                entry.priceError ||
                (entry.prices && entry.prices.status !== 'available'
                  ? entry.prices.message
                  : undefined);
              return (
                <div
                  className="assessment-inspect-assessment__full-period"
                  key={entry.period.index}
                >
                  <div>
                    <Typography.Text strong>
                      {entry.period.label}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {formatAssessmentDate(entry.period.scenario_date)}
                    </Typography.Text>
                  </div>
                  {(entry.priceLoading || entry.newsLoading) && (
                    <Spin size="small" />
                  )}
                  {priceIssue && (
                    <Tooltip title={priceIssue}>
                      <span
                        aria-label={`${entry.period.label} price data information`}
                        className="assessment-inspect-assessment__full-period-info"
                        role="img"
                        tabIndex={0}
                      >
                        <InfoCircleOutlined />
                      </span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
          <div className="assessment-inspect-assessment__full-chart">
            {chart.series.length > 0 ? (
              <Chart
                height="100%"
                options={options}
                series={chart.series}
                type="line"
              />
            ) : (
              <div className="assessment-inspect-assessment__full-chart-empty">
                {allPricesSettled ? (
                  <Typography.Text type="secondary">
                    No chartable PM quotes are available for this instrument.
                  </Typography.Text>
                ) : (
                  <>
                    <Spin size="small" />
                    <Typography.Text type="secondary">
                      Loading shared period prices…
                    </Typography.Text>
                  </>
                )}
              </div>
            )}
            <div
              aria-hidden="true"
              className="assessment-inspect-assessment__full-period-guides"
              style={{
                gridTemplateColumns: `repeat(${entries.length}, ${PERIOD_WIDTH}px)`,
                left: AXIS_GUTTER,
                right: AXIS_GUTTER,
              }}
            >
              {entries.map(entry => (
                <span key={entry.period.index} />
              ))}
            </div>
          </div>
          <div
            className="assessment-inspect-assessment__full-news-track"
            style={alignedGridStyle}
          >
            {entries.map(entry => {
              const news = state.periodNewsByIndex[entry.period.index];
              const metrics = periodMetricsByIndex.get(entry.period.index);
              const rows = metrics?.rows ?? [];
              const otherUserSelectedRows =
                metrics?.otherUserSelectedRows ?? [];
              const foregroundNewsError =
                state.periodNewsPeriodIndex === entry.period.index
                  ? state.periodNewsError
                  : undefined;
              return (
                <section
                  aria-label={`Non-neutral news of ${entry.period.label} for ${instrument.code}`}
                  className="assessment-inspect-assessment__full-period-news"
                  key={entry.period.index}
                >
                  <div className="assessment-inspect-assessment__full-news-heading-row">
                    <Typography.Text
                      className="assessment-inspect-assessment__full-news-heading"
                      strong
                    >
                      Non-neutral news ({rows.length})
                      {hasSelectedUserAttempt
                        ? ` · User selected (${metrics?.userSelectedNonNeutralNewsCount ?? 0})`
                        : ''}
                    </Typography.Text>
                    <div className="assessment-inspect-assessment__full-news-metrics">
                      <Tag
                        bordered={false}
                        className="assessment-inspect-assessment__full-news-metric-tag"
                        color={
                          metrics?.icsSum !== undefined && metrics.icsSum > 0
                            ? 'green'
                            : metrics?.icsSum !== undefined &&
                                metrics.icsSum < 0
                              ? 'red'
                              : undefined
                        }
                      >
                        ICS SUM {formatIcsSum(metrics?.icsSum)}
                      </Tag>
                      <PeriodChangeTag
                        change={{
                          key: `price-${entry.period.index}`,
                          label: 'PRICE',
                          unit: '%',
                          value: metrics?.pricePercentage,
                        }}
                      />
                    </div>
                  </div>
                  {entry.newsLoading ? (
                    <div className="assessment-inspect-assessment__full-news-state">
                      <Spin size="small" />
                    </div>
                  ) : foregroundNewsError ? (
                    <Typography.Text type="danger">
                      {foregroundNewsError}
                    </Typography.Text>
                  ) : !news ? (
                    <Typography.Text type="secondary">
                      Waiting for shared news data…
                    </Typography.Text>
                  ) : (
                    <div className="assessment-inspect-assessment__full-news-list">
                      {rows.length === 0 ? (
                        <Typography.Text type="secondary">
                          No non-neutral news
                        </Typography.Text>
                      ) : (
                        rows.map(row => (
                          <UserNewsItem
                            instrumentCode={instrument.code}
                            key={row.news.id}
                            row={row}
                            selectionStyle="highlight"
                            sourcePeriodIndex={entry.period.index}
                          />
                        ))
                      )}
                      {hasSelectedUserAttempt && (
                        <>
                          <Typography.Text
                            className="assessment-inspect-assessment__full-other-news-heading"
                            strong
                          >
                            Other user-selected news (
                            {otherUserSelectedRows.length})
                          </Typography.Text>
                          {userAssignmentsFetching ? (
                            <Typography.Text type="secondary">
                              Loading user selections…
                            </Typography.Text>
                          ) : otherUserSelectedRows.length === 0 ? (
                            <Typography.Text type="secondary">
                              No other user-selected news
                            </Typography.Text>
                          ) : (
                            otherUserSelectedRows.map(row => (
                              <UserNewsItem
                                instrumentCode={instrument.code}
                                key={row.news.id}
                                row={row}
                                selectionStyle="incorrect"
                                sourcePeriodIndex={entry.period.index}
                              />
                            ))
                          )}
                        </>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullAssessmentWorkspace;
